import { useEffect, useMemo, useState } from "react";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import localeText from "../../../../locale";
import AdminService from "../../../../service/AdminService";
import useAdminList, { AdminApiError, expectCode } from "../../hooks/useAdminList";
import useAdminAction from "../../hooks/useAdminAction";
import { buildSearchText, formatDay, getToken } from "../../utils/adminUtils";
import StatusTabs from "../../components/list/StatusTabs";
import CatalogTable from "../../components/list/CatalogTable";
import StatusBadge from "../../components/list/StatusBadge";
import FormDialog from "../../components/form/FormDialog";
import FormField from "../../components/form/FormField";
import ThemeDialog from "./ThemeDialog";
import "./AdminWhitelabel.css";

const TABS = { ALL: "all", ACTIVE: "active", INACTIVE: "inactive" };

// A API devolve a data de criação como array [ano, mês, dia, hora, min, seg]
const toDate = (value) => {
  if (Array.isArray(value)) {
    const [y, m, d, h = 0, min = 0, s = 0] = value;
    return new Date(y, m - 1, d, h, min, s);
  }
  return value ? new Date(value) : null;
};

const loadSubnets = async () => expectCode(await new AdminService().getAllSubnets(getToken()), 200000).result;

const expectOneOf = (res, codes) => {
  if (!codes.includes(res?.messageCode)) throw new AdminApiError(res?.messageCode ?? 500000);
  return res;
};

const emptyCreate = { name: "", description: "", manager: null, errors: {} };

const AdminWhitelabel = () => {
  const { items, loading, loadError, reload } = useAdminList(loadSubnets);
  const { run, runningId } = useAdminAction();
  const [tab, setTab] = useState(TABS.ALL);
  const [creating, setCreating] = useState(null);
  const [editing, setEditing] = useState(null);
  const [themeSubnet, setThemeSubnet] = useState(null);
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);

  const rows = useMemo(
    () =>
      items.map((subnet) => {
        const created = toDate(subnet.createdDate);
        return {
          ...subnet,
          createdValue: created ? created.getTime() : 0,
          createdLabel: created ? formatDay(created) : "—",
          memberValue: Number(subnet.memberCount) || 0,
          searchText: buildSearchText([
            String(subnet.id ?? ""),
            subnet.name,
            subnet.description,
            subnet.ownerName,
            subnet.username,
          ]),
        };
      }),
    [items],
  );

  const counts = useMemo(
    () => ({
      [TABS.ALL]: rows.length,
      [TABS.ACTIVE]: rows.filter((row) => row.active).length,
      [TABS.INACTIVE]: rows.filter((row) => !row.active).length,
    }),
    [rows],
  );

  const visibleRows = useMemo(() => {
    if (tab === TABS.ACTIVE) return rows.filter((row) => row.active);
    if (tab === TABS.INACTIVE) return rows.filter((row) => !row.active);
    return rows;
  }, [rows, tab]);

  // Managers só são carregados quando o diálogo de criação abre, como no clássico
  useEffect(() => {
    if (!creating || managers.length || loadingManagers) return;
    setLoadingManagers(true);
    new AdminService()
      .getAreaManagers(getToken())
      .then((res) => {
        if (res?.messageCode !== 200001) return;
        setManagers(
          (res.result?.userListJSONs || [])
            .filter((user) => !user.cancelled)
            .map((user) => ({
              value: user.username,
              name: `${user.firstname || ""} ${user.surname || ""}`.trim() || user.username,
              username: user.username,
              email: user.email,
              searchLabel: [user.username, user.firstname, user.surname, user.email]
                .filter(Boolean)
                .join(" "),
            })),
        );
      })
      .catch((e) => console.log(e))
      .finally(() => setLoadingManagers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creating]);

  const create = async () => {
    const errors = {};
    if (!creating.name.trim()) errors.name = localeText("pleaseEnterName");
    if (!creating.description.trim()) errors.description = localeText("pleaseEnterDescription");
    if (!creating.manager) errors.manager = localeText("pleaseSelectManager");
    if (Object.keys(errors).length) {
      setCreating((prev) => ({ ...prev, errors }));
      return;
    }
    const ok = await run(
      "create",
      async () =>
        expectCode(
          await new AdminService().createSubnet(
            getToken(),
            creating.name.trim(),
            creating.description.trim(),
            creating.manager,
          ),
          200001,
        ),
      "whitelabelSubnetCreatedSuccessfully",
    );
    if (ok) {
      setCreating(null);
      reload();
    }
  };

  const saveEdit = async () => {
    const errors = {};
    if (!editing.name.trim()) errors.name = localeText("pleaseEnterName");
    if (!editing.description.trim()) errors.description = localeText("pleaseEnterDescription");
    if (Object.keys(errors).length) {
      setEditing((prev) => ({ ...prev, errors }));
      return;
    }
    const ok = await run(
      "edit",
      async () =>
        expectOneOf(
          await new AdminService().editSubnet(
            getToken(),
            editing.subnet.id,
            editing.name.trim(),
            editing.description.trim(),
          ),
          [200000, 200001],
        ),
      "subnetEditedSuccessfully",
    );
    if (ok) {
      setEditing(null);
      reload();
    }
  };

  const textField = (state, setState, field, label, id) => (
    <FormField label={label} htmlFor={id} error={state.errors[field]} required>
      <input
        id={id}
        className="admin-input"
        value={state[field]}
        onChange={(e) =>
          setState((prev) => ({
            ...prev,
            [field]: e.target.value,
            errors: { ...prev.errors, [field]: null },
          }))
        }
        autoComplete="off"
        autoFocus={field === "name"}
      />
    </FormField>
  );

  return (
    <div className="admin-list">
      <ThemeDialog subnet={themeSubnet} onClose={() => setThemeSubnet(null)} />

      <FormDialog
        visible={!!creating}
        title={`${localeText("add")} ${localeText("subnet")}`}
        saving={runningId === "create"}
        onSave={create}
        onCancel={() => setCreating(null)}
        width="32rem"
      >
        {creating && (
          <>
            {textField(creating, setCreating, "name", localeText("name"), "subnet-name")}
            {textField(creating, setCreating, "description", localeText("description"), "subnet-description")}
            <FormField
              label="Manager"
              htmlFor="subnet-manager"
              hint={localeText("searchByUsernameNameOrEmail")}
              error={creating.errors.manager}
              required
            >
              <Dropdown
                inputId="subnet-manager"
                value={creating.manager}
                options={managers}
                optionLabel="searchLabel"
                valueTemplate={(option, props) =>
                  option ? `${option.name} · @${option.username}` : props.placeholder
                }
                itemTemplate={(option) => (
                  <div className="admin-stack">
                    <span className="admin-strong">{option.name}</span>
                    <span className="admin-muted">
                      @{option.username}
                      {option.email ? ` · ${option.email}` : ""}
                    </span>
                  </div>
                )}
                onChange={(e) =>
                  setCreating((prev) => ({
                    ...prev,
                    manager: e.value,
                    errors: { ...prev.errors, manager: null },
                  }))
                }
                filter
                filterBy="searchLabel"
                showClear
                placeholder={localeText(loadingManagers ? "adminAddressLoadingUsers" : "selectManager")}
                emptyFilterMessage={localeText("adminListNoResults")}
                emptyMessage={localeText("noManagersFound")}
                disabled={loadingManagers}
                panelClassName="admin-multiselect-panel"
              />
            </FormField>
          </>
        )}
      </FormDialog>

      <FormDialog
        visible={!!editing}
        title={localeText("editSubnetData")}
        subtitle={editing?.subnet?.name}
        saving={runningId === "edit"}
        onSave={saveEdit}
        onCancel={() => setEditing(null)}
        width="32rem"
      >
        {editing && (
          <>
            {textField(editing, setEditing, "name", localeText("name"), "edit-subnet-name")}
            {textField(editing, setEditing, "description", localeText("description"), "edit-subnet-description")}
          </>
        )}
      </FormDialog>

      <StatusTabs
        tabs={[
          { value: TABS.ALL, label: localeText("adminUsersStatusAll") },
          { value: TABS.ACTIVE, label: localeText("adminWhitelabelActive") },
          { value: TABS.INACTIVE, label: localeText("adminWhitelabelInactive") },
        ]}
        value={tab}
        onChange={setTab}
        counts={counts}
        loading={loading}
      />

      <CatalogTable
        key={tab}
        rows={visibleRows}
        loading={loading}
        loadError={loadError}
        onReload={reload}
        searchPlaceholder={localeText("adminWhitelabelSearch")}
        createLabel={`${localeText("add")} ${localeText("subnet")}`}
        onCreate={() => setCreating(emptyCreate)}
        sortField="createdValue"
        sortOrder={-1}
        minWidth="58rem"
        emptyIcon="pi pi-sitemap"
      >
        <Column
          header={localeText("subnet")}
          field="name"
          body={(subnet) => (
            <div className="admin-stack">
              <span className="admin-strong">{subnet.name || "—"}</span>
              {subnet.description && (
                <span className="admin-muted admin-whitelabel__description">{subnet.description}</span>
              )}
              <span className="admin-muted">ID {subnet.id}</span>
            </div>
          )}
          sortable
        />
        <Column
          header={localeText("ownerName")}
          field="ownerName"
          body={(subnet) => (
            <div className="admin-stack">
              <span>{subnet.ownerName || "—"}</span>
              {subnet.username && <span className="admin-muted">@{subnet.username}</span>}
            </div>
          )}
          sortable
        />
        <Column
          header={localeText("memberCount")}
          field="memberValue"
          body={(subnet) => <span className="admin-num">{subnet.memberValue}</span>}
          sortable
        />
        <Column
          header={localeText("adminBackofficeDateCreation")}
          field="createdValue"
          body={(subnet) => subnet.createdLabel}
          sortable
        />
        <Column
          header="Status"
          field="active"
          body={(subnet) =>
            subnet.active ? (
              <StatusBadge tone="good">{localeText("adminWhitelabelActive")}</StatusBadge>
            ) : (
              <StatusBadge tone="neutral">{localeText("adminWhitelabelInactive")}</StatusBadge>
            )
          }
          sortable
        />
        <Column
          header={localeText("actions")}
          frozen
          alignFrozen="right"
          headerClassName="admin-list__actions-cell"
          bodyClassName="admin-list__actions-cell"
          body={(subnet) => (
            <div className="admin-list__header-actions">
              <button
                type="button"
                className="admin-icon-btn"
                onClick={() =>
                  setEditing({
                    subnet,
                    name: subnet.name || "",
                    description: subnet.description || "",
                    errors: {},
                  })
                }
                title={localeText("editSubnetData")}
                aria-label={localeText("editSubnetData")}
              >
                <i className="pi pi-pencil" aria-hidden="true" />
              </button>
              <button type="button" className="admin-btn" onClick={() => setThemeSubnet(subnet)}>
                <i className="pi pi-palette" aria-hidden="true" />
                {localeText("adminWhitelabelTheme")}
              </button>
            </div>
          )}
        />
      </CatalogTable>
    </div>
  );
};

export default AdminWhitelabel;
